import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { IMAGES, CONTACT } from "../constants/contact";

const EASE = [0.76, 0, 0.24, 1];

export const Hero = ({ t }) => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 1000], [0, 260]);
  const contentOpacity = useTransform(scrollY, [0, 500], [1, 0]);
  const contentY = useTransform(scrollY, [0, 500], [0, -80]);

  return (
    <section id="top" className="relative flex h-screen flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={IMAGES.hero}
          alt="Pesca ao pôr do sol na barragem de Alqueva"
          className="h-full w-full object-cover"
          data-testid="hero-background-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/20" />
      </motion.div>

      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 lg:px-10"
      >
        <div className="overflow-hidden">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
            className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-gold"
            data-testid="hero-kicker"
          >
            {t.hero.kicker}
          </motion.p>
        </div>

        <h1 className="font-hero text-[clamp(3.5rem,12vw,11rem)] leading-[0.95] tracking-wide text-bone" data-testid="hero-title">
          {t.hero.titleLines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1 pt-[0.14em]">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.45 + i * 0.14, ease: EASE }}
                className={`block ${i === t.hero.titleLines.length - 1 ? "text-gold" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="max-w-md text-base leading-relaxed text-bone/70 sm:text-lg"
            data-testid="hero-subtitle"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25, ease: EASE }}
            className="flex flex-wrap items-center gap-4"
          >
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-book"
              className="group inline-flex items-center gap-3 rounded-sm bg-gold px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-bone"
            >
              {t.hero.ctaPrimary}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <a
              href="#especies"
              data-testid="hero-cta-species"
              className="group inline-flex items-center gap-3 rounded-sm border border-bone/20 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone/80 backdrop-blur-sm transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              {t.hero.ctaSecondary}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="mt-14 flex items-center justify-between border-t border-bone/10 pt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/40"
        >
          <span data-testid="hero-coordinates">{t.hero.coords}</span>
          <span className="flex items-center gap-2" data-testid="hero-scroll-hint">
            {t.hero.scroll}
            <ArrowDown className="h-3 w-3 animate-bounce text-gold" />
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
};
